"use client"

import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Loader2, TrendingUp, BarChart3, Table2, AlertTriangle } from "lucide-react"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea
} from 'recharts'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

// API Response Interfaces
interface ForecastStatsResponse {
  comid: number
  startdate: string
  enddate: string
  gendate: string
  region: string
  time_series: {
    datetime: string[]
    datetime_high_res: string[]
    high_res: number[]
  }
  units: { long: string; name: string; short: string }
}

interface ForecastEnsemblesResponse {
  comid: number
  startdate: string
  enddate: string
  gendate: string
  region: string
  time_series: {
    datetime: string[]
    datetime_high_res: string[]
    [key: string]: number[] | string[] // For ensemble members like "ensemble_01_m^3/s"
  }
  units: { long: string; name: string; short: string }
}

interface ForecastRecordsResponse {
  comid: number
  startdate: string
  enddate: string
  gendate: string
  region: string
  time_series: {
    datetime: string[]
    flow: number[]
  }
  units: { long: string; name: string; short: string }
}

interface ReturnPeriodsResponse {
  comid: number
  startdate: string
  enddate: string
  gendate: string
  region: string
  return_periods: {
    return_period_2: number
    return_period_5: number
    return_period_10: number
    return_period_25: number
    return_period_50: number
    return_period_100: number
  }
  units: { long: string; name: string; short: string }
}

// Combined data for visualization
interface ChartDataPoint {
  datetime: string
  high_res?: number
  ensemble_mean?: number
  ensemble_25?: number
  ensemble_75?: number
  historical?: number
  return_period_2?: number
  return_period_5?: number
  return_period_10?: number
  return_period_25?: number
  return_period_50?: number
  return_period_100?: number
}

interface EnhancedForecastProps {
  reachId: string | null
  onClose?: () => void
}

export function EnhancedForecast({ reachId, onClose }: EnhancedForecastProps) {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [showReturnPeriods, setShowReturnPeriods] = useState(true)
  const [showEnsembleRange, setShowEnsembleRange] = useState(true)
  const [showHighRes, setShowHighRes] = useState(true)
  const [showEnsembleMean, setShowEnsembleMean] = useState(true)
  const [metadata, setMetadata] = useState<{
    comid: number
    region: string
    startdate: string
    enddate: string
    gendate: string
    units: { long: string; name: string; short: string }
    returnPeriods: ReturnPeriodsResponse['return_periods']
  } | null>(null)

  useEffect(() => {
    if (!reachId) {
      setChartData([])
      setError(null)
      setMetadata(null)
      return
    }

    const fetchAllForecastData = async () => {
      setLoading(true)
      setError(null)
      
      try {
        // Use simple API calls without date parameters - they work with latest available data
        const [statsResponse, ensemblesResponse, recordsResponse, returnPeriodsResponse] = await Promise.all([
          fetch(`https://geoglows.ecmwf.int/api/ForecastStats/?reach_id=${reachId}&return_format=json`),
          fetch(`https://geoglows.ecmwf.int/api/ForecastEnsembles/?reach_id=${reachId}&return_format=json`),
          fetch(`https://geoglows.ecmwf.int/api/ForecastRecords/?reach_id=${reachId}&return_format=json`),
          fetch(`https://geoglows.ecmwf.int/api/ReturnPeriods/?reach_id=${reachId}&return_format=json`)
        ])

        if (!statsResponse.ok || !ensemblesResponse.ok || !recordsResponse.ok || !returnPeriodsResponse.ok) {
          throw new Error('Failed to fetch forecast data')
        }

        const [statsData, ensemblesData, , returnPeriodsData] = await Promise.all([
          statsResponse.json() as Promise<ForecastStatsResponse>,
          ensemblesResponse.json() as Promise<ForecastEnsemblesResponse>,
          recordsResponse.json() as Promise<ForecastRecordsResponse>,
          returnPeriodsResponse.json() as Promise<ReturnPeriodsResponse>
        ])

        // Process ensemble data to get statistics
        const ensembleMembers: number[][] = []
        Object.keys(ensemblesData.time_series).forEach(key => {
          if (key.startsWith('ensemble_') && key.includes('m^3/s')) {
            ensembleMembers.push(ensemblesData.time_series[key] as number[])
          }
        })

        // Calculate ensemble statistics
        const ensembleMeans: number[] = []
        const ensemble25Percentiles: number[] = []
        const ensemble75Percentiles: number[] = []

        if (ensembleMembers.length > 0) {
          for (let i = 0; i < ensembleMembers[0].length; i++) {
            const values = ensembleMembers.map(member => member[i]).filter(v => v != null).sort((a, b) => a - b)
            
            if (values.length > 0) {
              ensembleMeans.push(values.reduce((sum, val) => sum + val, 0) / values.length)
              ensemble25Percentiles.push(values[Math.floor(values.length * 0.25)])
              ensemble75Percentiles.push(values[Math.floor(values.length * 0.75)])
            }
          }
        }

        // Combine all data into chart format
        const combinedData: ChartDataPoint[] = statsData.time_series.datetime_high_res.map((datetime, index) => ({
          datetime,
          high_res: statsData.time_series.high_res[index],
          ensemble_mean: index < ensembleMeans.length ? ensembleMeans[index] : undefined,
          ensemble_25: index < ensemble25Percentiles.length ? ensemble25Percentiles[index] : undefined,
          ensemble_75: index < ensemble75Percentiles.length ? ensemble75Percentiles[index] : undefined,
          return_period_2: returnPeriodsData.return_periods.return_period_2,
          return_period_5: returnPeriodsData.return_periods.return_period_5,
          return_period_10: returnPeriodsData.return_periods.return_period_10,
          return_period_25: returnPeriodsData.return_periods.return_period_25,
          return_period_50: returnPeriodsData.return_periods.return_period_50,
          return_period_100: returnPeriodsData.return_periods.return_period_100,
        }))

        setChartData(combinedData)
        setMetadata({
          comid: statsData.comid,
          region: statsData.region,
          startdate: statsData.startdate,
          enddate: statsData.enddate,
          gendate: statsData.gendate,
          units: statsData.units,
          returnPeriods: returnPeriodsData.return_periods
        })

      } catch (err) {
        console.error('Error fetching forecast data:', err)
        let errorMessage = 'Failed to fetch forecast data'
        
        if (err instanceof Error) {
          errorMessage = err.message
          // Add helpful context for common API errors
          if (err.message.includes('404') || err.message.includes('not found')) {
            errorMessage += '. Note: Forecasts are only available for recent dates. Please select a date within the last few days to 15 days in the future.'
          }
        }
        
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    fetchAllForecastData()
  }, [reachId])

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  const formatFlow = (value: number) => {
    return value.toFixed(1)
  }

  // Calculate dynamic Y-axis maximum based on visible elements
  const calculateYAxisMax = (dataMax: number) => {
    if (!metadata) return dataMax * 1.1

    let maxValues = []

    // Always include streamflow data with padding
    maxValues.push(dataMax * 1.1)

    // Always consider return periods for axis scaling - the toggle only affects shaded bands
    // Determine which return periods should be shown based on data proximity
    const returnPeriodValues = [
      metadata.returnPeriods.return_period_2,
      metadata.returnPeriods.return_period_5,
      metadata.returnPeriods.return_period_10,
      metadata.returnPeriods.return_period_25,
      metadata.returnPeriods.return_period_50,
      metadata.returnPeriods.return_period_100
    ]

    if (showReturnPeriods) {
      // When bands are enabled, include all return periods
      const maxReturnPeriod = Math.max(...returnPeriodValues)
      maxValues.push(maxReturnPeriod * 1.1)
    } else {
      // When bands are disabled, still show return periods that are close to the data
      const minReturnPeriod = metadata.returnPeriods.return_period_2
      if (dataMax > minReturnPeriod * 0.3) {
        // Find the most relevant return period to show
        const relevantReturnPeriods = returnPeriodValues.filter(rp => rp <= dataMax * 3)
        if (relevantReturnPeriods.length > 0) {
          maxValues.push(Math.max(...relevantReturnPeriods) * 1.2)
        } else {
          // If data is very low, at least show the 2-year return period
          maxValues.push(metadata.returnPeriods.return_period_2 * 1.1)
        }
      }
    }

    return Math.max(...maxValues)
  }

  // Determine which return period lines should be visible based on current Y-axis range
  const getVisibleReturnPeriods = () => {
    if (!metadata || !chartData.length) return []

    const dataMax = Math.max(...chartData.map(d => Math.max(d.high_res || 0, d.ensemble_mean || 0, d.ensemble_75 || 0)))
    const yAxisMax = calculateYAxisMax(dataMax)
    
    const returnPeriods = [
      { key: 'return_period_2', value: metadata.returnPeriods.return_period_2, label: '2 Year', color: '#facc15' },
      { key: 'return_period_5', value: metadata.returnPeriods.return_period_5, label: '5 Year', color: '#f59e0b' },
      { key: 'return_period_10', value: metadata.returnPeriods.return_period_10, label: '10 Year', color: '#f97316' },
      { key: 'return_period_25', value: metadata.returnPeriods.return_period_25, label: '25 Year', color: '#dc2626' },
      { key: 'return_period_50', value: metadata.returnPeriods.return_period_50, label: '50 Year', color: '#b91c1c' },
      { key: 'return_period_100', value: metadata.returnPeriods.return_period_100, label: '100 Year', color: '#9333ea' },
    ]

    // Always show return period lines that are within the visible Y-axis range
    return returnPeriods.filter(rp => rp.value <= yAxisMax * 0.95)
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border rounded-lg p-3 shadow-lg">
          <p className="font-medium">{formatDate(label)}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatFlow(entry.value)} {metadata?.units.short}
            </p>
          ))}
        </div>
      )
    }
    return null
  }

  if (!reachId) {
    return null
  }

  return (
    <Card className="w-full max-w-7xl">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex-1">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            GEOGLOWS Streamflow Forecast
          </CardTitle>
          <div className="text-sm text-muted-foreground mt-1 space-y-1">
            <p>Reach ID: {reachId} {metadata && `(COMID: ${metadata.comid})`}</p>
            <p>Using latest available forecast data</p>
            {metadata && (
              <>
                <p>Region: {metadata.region.replace('_', ' ').replace('-geoglows', '').toUpperCase()}</p>
                <p>Generated: {formatDate(metadata.gendate)}</p>
              </>
            )}
          </div>
        </div>
        

        {onClose && (
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-xl"
          >
            ×
          </button>
        )}
      </CardHeader>
      
      <CardContent>
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading forecast data...</span>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center py-8 text-destructive">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>Error: {error}</span>
          </div>
        )}

        {!loading && !error && chartData.length > 0 && (
          <Tabs defaultValue="chart" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="chart" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Chart View
              </TabsTrigger>
              <TabsTrigger value="table" className="flex items-center gap-2">
                <Table2 className="h-4 w-4" />
                Data Table
              </TabsTrigger>
            </TabsList>

            <TabsContent value="chart" className="space-y-4">
              {/* Chart Controls */}
              <div className="flex flex-wrap gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="return-periods"
                    checked={showReturnPeriods}
                    onCheckedChange={setShowReturnPeriods}
                  />
                  <Label htmlFor="return-periods" className="text-sm">Return Period Bands</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="ensemble-range"
                    checked={showEnsembleRange}
                    onCheckedChange={setShowEnsembleRange}
                  />
                  <Label htmlFor="ensemble-range" className="text-sm">25-75% Range</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="ensemble-mean"
                    checked={showEnsembleMean}
                    onCheckedChange={setShowEnsembleMean}
                  />
                  <Label htmlFor="ensemble-mean" className="text-sm">Ensemble Average</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="high-res"
                    checked={showHighRes}
                    onCheckedChange={setShowHighRes}
                  />
                  <Label htmlFor="high-res" className="text-sm">High Resolution</Label>
                </div>
              </div>
              
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="datetime" 
                      tickFormatter={formatDate}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      label={{ value: `Streamflow (${metadata?.units.short})`, angle: -90, position: 'insideLeft' }}
                      domain={[0, calculateYAxisMax]}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    
                    {/* Return period threshold lines - dynamically visible */}
                    {getVisibleReturnPeriods().map(rp => (
                      <ReferenceLine 
                        key={rp.key}
                        y={rp.value} 
                        stroke={rp.color} 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        label={rp.label}
                      />
                    ))}

                    {/* Return period shaded areas - only show if bands are enabled */}
                    {showReturnPeriods && metadata && (() => {
                      const dataMax = Math.max(...chartData.map(d => Math.max(d.high_res || 0, d.ensemble_mean || 0, d.ensemble_75 || 0)))
                      const yAxisMax = calculateYAxisMax(dataMax)
                      
                      const bands = [
                        { y1: metadata.returnPeriods.return_period_100, y2: yAxisMax, fill: "#9333ea", name: "100+ Year" },
                        { y1: metadata.returnPeriods.return_period_50, y2: metadata.returnPeriods.return_period_100, fill: "#b91c1c", name: "50-100 Year" },
                        { y1: metadata.returnPeriods.return_period_25, y2: metadata.returnPeriods.return_period_50, fill: "#dc2626", name: "25-50 Year" },
                        { y1: metadata.returnPeriods.return_period_10, y2: metadata.returnPeriods.return_period_25, fill: "#ea580c", name: "10-25 Year" },
                        { y1: metadata.returnPeriods.return_period_5, y2: metadata.returnPeriods.return_period_10, fill: "#f97316", name: "5-10 Year" },
                        { y1: metadata.returnPeriods.return_period_2, y2: metadata.returnPeriods.return_period_5, fill: "#eab308", name: "2-5 Year" },
                      ]
                      
                      return bands.filter(band => band.y1 <= yAxisMax * 0.98).map((band, index) => (
                        <ReferenceArea
                          key={index}
                          y1={band.y1}
                          y2={Math.min(band.y2, yAxisMax)}
                          fill={band.fill}
                          fillOpacity={0.15}
                        />
                      ))
                    })()}

                    {/* Ensemble percentiles - only show if enabled */}
                    {showEnsembleRange && (
                      <>
                        <Line 
                          type="monotone" 
                          dataKey="ensemble_25" 
                          stroke="#8dd1e1" 
                          strokeWidth={1}
                          strokeDasharray="5 5"
                          name="25th Percentile" 
                          dot={false}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="ensemble_75" 
                          stroke="#8dd1e1" 
                          strokeWidth={1}
                          strokeDasharray="5 5"
                          name="75th Percentile" 
                          dot={false}
                        />
                      </>
                    )}
                    
                    {/* Main forecast lines - conditional */}
                    {showEnsembleMean && (
                      <Line 
                        type="monotone" 
                        dataKey="ensemble_mean" 
                        stroke="#2563eb" 
                        strokeWidth={2}
                        name="Ensemble Average" 
                        dot={false}
                      />
                    )}
                    {showHighRes && (
                      <Line 
                        type="monotone" 
                        dataKey="high_res" 
                        stroke="#000000" 
                        strokeWidth={3}
                        name="High Resolution Forecast" 
                        dot={false}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Return periods legend - dynamically visible */}
              {metadata && getVisibleReturnPeriods().length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Return Period Thresholds {showReturnPeriods && '(with bands)'}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {getVisibleReturnPeriods().map(rp => (
                      <div key={rp.key} className="flex items-center gap-2">
                        <div className="w-4 h-1 rounded" style={{ backgroundColor: rp.color }}></div>
                        <span className="text-sm">{rp.label}: {formatFlow(rp.value)} {metadata.units.short}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="table" className="space-y-4">
              <div className="text-sm text-muted-foreground">
                <p>Showing {chartData.length} hourly forecast records</p>
                {metadata && (
                  <p>Units: {metadata.units.long} ({metadata.units.short})</p>
                )}
              </div>
              
              <div className="rounded-md border max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date/Time</TableHead>
                      <TableHead>High Resolution</TableHead>
                      <TableHead>Ensemble Mean</TableHead>
                      <TableHead>25th Percentile</TableHead>
                      <TableHead>75th Percentile</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {chartData.slice(0, 50).map((record) => (
                      <TableRow key={record.datetime}>
                        <TableCell className="font-medium">
                          {formatDate(record.datetime)}
                        </TableCell>
                        <TableCell className="font-mono">
                          {record.high_res ? formatFlow(record.high_res) : '-'}
                        </TableCell>
                        <TableCell className="font-mono">
                          {record.ensemble_mean ? formatFlow(record.ensemble_mean) : '-'}
                        </TableCell>
                        <TableCell className="font-mono">
                          {record.ensemble_25 ? formatFlow(record.ensemble_25) : '-'}
                        </TableCell>
                        <TableCell className="font-mono">
                          {record.ensemble_75 ? formatFlow(record.ensemble_75) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {chartData.length > 50 && (
                <p className="text-sm text-muted-foreground text-center">
                  Showing first 50 records of {chartData.length} total
                </p>
              )}
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  )
}